import { cn } from '@/lib/utils';
import { WifiOff, Cloud, Loader2 } from 'lucide-react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NetworkStatusProps {
  className?: string;
}

export function NetworkStatus({ className }: NetworkStatusProps) {
  const { t } = useTranslation('operator');
  const { isOnline, pendingCount, sync } = useNetworkStatus();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    if (isSyncing || !isOnline || pendingCount === 0) return;

    setIsSyncing(true);
    try {
      await sync();
    } finally {
      setIsSyncing(false);
    }
  };

  // Don't show anything when online with no pending items
  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-20 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-2 px-4 py-2 rounded-full shadow-lg',
        'text-sm font-medium transition-all duration-300',
        isOnline
          ? 'bg-primary text-white'
          : 'bg-amber-500 text-white',
        className
      )}
    >
      {!isOnline ? (
        <>
          <WifiOff className="w-4 h-4" />
          <span>{t('networkStatus.offline')}</span>
          {pendingCount > 0 && (
            <span className="ms-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
              {t('networkStatus.pending', { count: pendingCount })}
            </span>
          )}
        </>
      ) : pendingCount > 0 ? (
        <>
          {isSyncing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Cloud className="w-4 h-4" />
          )}
          <span>{t('networkStatus.pending', { count: pendingCount })}</span>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="ms-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded-full text-xs transition-colors disabled:opacity-50"
          >
            {isSyncing ? t('networkStatus.syncing') : t('networkStatus.sync')}
          </button>
        </>
      ) : null}
    </div>
  );
}
